import { useEffect, useRef, useState, ReactNode, type FormEvent, type KeyboardEvent } from "react";
import { TextInput, Button, Tooltip } from "@alku/ui-kit";
import MarkdownRenderer from "../../vendor/markdownRenderer";
import type { ChatInterfaceLabels, ChatMessage } from "../../types";
import { formatMessageTime } from "../../utils";
/** @ts-ignore */
import avatar from "url:../../assets/cactai.png";
import { StopOutlined, CopyOutlined, CheckOutlined, LikeOutlined, DislikeOutlined, LikeFilled, DislikeFilled, SendOutlined, LoadingOutlined } from '@ant-design/icons';
import { sendFeedback } from "../../services/FeedbackService";

const defaultLabels: Required<ChatInterfaceLabels> = {
  inputPlaceholder: "Type a message…",
  sendButton: "Send",
  emptyState: "Start the conversation",
  typingIndicator: "CactAI is thinking...",
};

const CopyButton = ({ text }: { text: string }) =>
{
  const [copied, setCopyState] = useState(false)
  async function Copy()
  {
    await navigator.clipboard.writeText(text)
    setCopyState(true)
    setTimeout(() => {
      setCopyState(false)
    }, 3000)
  }

  return (
    <div className="copy-button-container" onClick={() => Copy()}>
      { copied ? <Tooltip id="copy" content="Copied"><CheckOutlined className="copy-icon"/></Tooltip> : <Tooltip id="copy" content="Copy"><CopyOutlined className="copy-icon"/></Tooltip> }
    </div>
  )
}

const FeedbackContainer = ({ messageID }: { messageID: string }) => {
  const [feedback, setFeedback] = useState<string | null>(null);

  const updateFeedback = function(feedback: 'liked' | 'disliked' | null) {
    setFeedback(feedback);
    sendFeedback(messageID, feedback as 'liked' | 'disliked' | null);
  }
      
  return (
    <div className="feedback-container">
      {feedback === 'disliked' ? <Tooltip id={"dislike"} content="Dislike"><DislikeFilled onClick={() => updateFeedback(null)} /></Tooltip> : <Tooltip id={"dislike"} content="Dislike"><DislikeOutlined onClick={() => updateFeedback('disliked')} /></Tooltip>}
      {feedback === 'liked' ? <Tooltip id={"like"} content="Like"><LikeFilled onClick={() => updateFeedback(null)} /></Tooltip> : <Tooltip id={"like"} content="Like"><LikeOutlined onClick={() => updateFeedback('liked')} /></Tooltip>}
    </div>
  )
}

export interface MessageListProps {
  messages: ChatMessage[];
  emptyLabel: string;
  renderMessage?: (message: ChatMessage) => ReactNode;
  isLoading?: boolean;
}

export function MessageList({
  messages,
  emptyLabel,
  renderMessage,
  isLoading,
}: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [typingLabel, setTypingLabel] = useState("CactAI is thinking...");
  const lastScrollKeyRef = useRef("");

  useEffect(() => {
    const last = messages[messages.length - 1];
    const scrollKey = `${messages.length}:${last?.id ?? ""}:${isLoading}`;
    if (scrollKey === lastScrollKeyRef.current) return;
    lastScrollKeyRef.current = scrollKey;

    const container = messagesContainerRef.current;
    if (!container) return;

    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    });
  }, [messages, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      setTypingLabel("CactAI is thinking...");
      return;
    }

    const id = window.setInterval(() => {
      setTypingLabel((label) => {
        if (label.endsWith("...")) return "CactAI is thinking.";
        if (label.endsWith("..")) return "CactAI is thinking...";
        if (label.endsWith(".")) return "CactAI is thinking..";
        return "CactAI is thinking...";
      });
    }, 1000);

    return () => window.clearInterval(id);
  }, [isLoading]);

  if (messages.length === 0 && !isLoading) {
    return (
      <div className="chat-empty" role="status">
        {emptyLabel}
      </div>
    );
  }

  return (
    <div
      ref={messagesContainerRef}
      className="chat-messages"
      role="log"
      aria-live="polite"
      aria-relevant="additions"
    >
      {messages.map((message) => (
        <article
          key={message.id}
          className={`chat-message chat-message--${message.role}`}
          data-status={message.status}
        >
          {renderMessage ? (
            renderMessage(message)
          ) : (
            <>
              {
                typeof message.content === "string" && message.content.length > 0
                ? <div className="chat-bubble">
                  <MarkdownRenderer markdown={message.content} />
                  {
                    message.role === "assistant" && <div className="bubble-footer"><FeedbackContainer messageID={message.id} /><CopyButton text={message.content}/></div>
                  }
                </div>
                : <div className="chat-bubble">No message</div>
              }
              {message.createdAt != null && (
                <time className="chat-time" dateTime={new Date(message.createdAt).toISOString()}>
                  {formatMessageTime(message.createdAt)}
                </time>
              )}
            </>
          )}
        </article>
      ))}
      {isLoading && (
        <div className="chat-typing" role="status" aria-busy="true">
          <span className="chat-typing-dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          {typingLabel}
        </div>
      )}
      <div ref={bottomRef} className="chat-scroll-anchor" />
    </div>
  );
}

export interface ChatInputProps {
  onSubmit: (content: string) => void | Promise<void>;
  disabled?: boolean;
  placeholder: string;
  sendLabel: string;
  autoFocus?: boolean;
  maxLength?: number;
  onCancel: () => void | Promise<void>;
}

export function ChatInput({
  onSubmit,
  disabled,
  placeholder,
  sendLabel,
  autoFocus,
  maxLength,
  onCancel,
}: ChatInputProps) {
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (autoFocus) {
      document.getElementById("cactai-chat-message")?.focus();
    }
  }, [autoFocus]);


  const cancelPrompt = () => {
    setValue("");
    setSubmitting(false);
    onCancel();
  };

  const handleSubmit = async (event?: FormEvent) => {
    event?.preventDefault();
    const trimmed = value.trim();
    if (!trimmed || disabled || submitting) return;

    setSubmitting(true);
    try {
      await onSubmit(trimmed);
      setValue("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void handleSubmit();
    }
  };

  return (
    <form className="chat-input-form" onSubmit={(e) => void handleSubmit(e)}>
      <img src={avatar} alt="CactAI" className="chat-avatar" />
      <TextInput
        id="cactai-chat-message"
        label={placeholder}
        noLabel
        multiline
        rows={1}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled || submitting}
        maxLength={maxLength}
        aria-label={placeholder}
      />
      <Button
        type={submitting ? "button" : "submit"}
        as="button"
        onClick={submitting ? () => void cancelPrompt() : undefined}
        style="function"
        className="chat-send-button"
        ariaLabel={sendLabel}
      >
        {submitting ? <StopOutlined /> : <SendOutlined />}
      </Button>
    </form>
  );
}

export function mergeLabels(labels?: ChatInterfaceLabels): Required<ChatInterfaceLabels> {
  return { ...defaultLabels, ...labels };
}
